import { blue, green, red, yellow } from "@mui/material/colors";
import { IRequest, RequestStatus, RequestType, ServiceType, SurgeryStatus } from "./types";

export type LabStatus = RequestStatus.COMPLETED | RequestStatus.CANCELED | RequestStatus.PENDING_PAYMENT | RequestStatus.PAYMENT_COMPLETED;
export const labStatusValues: LabStatus[] = [
    RequestStatus.COMPLETED,
    RequestStatus.CANCELED,
    RequestStatus.PENDING_PAYMENT,
    RequestStatus.PAYMENT_COMPLETED
];

/**
 * SHER-908: cordonnerie's own filter list. labStatusValues is SHARED by Laboratory,
 * Radiology AND Cordonnerie, so adding MATERIAL_DELIVERED there would put "Matériel
 * délivré" in the laboratory and radiology filter menus, where no request can ever hold it.
 */
export type ShoeStatus = LabStatus | RequestStatus.MATERIAL_DELIVERED;
export const shoeStatusValues: ShoeStatus[] = [
    ...labStatusValues,
    RequestStatus.MATERIAL_DELIVERED,
];

/** SHER-488: pharmacy stock-request workflow — not lab/billing statuses. */
export type PharmacyStatus =
    | RequestStatus.PENDING_APPROVAL
    | RequestStatus.IN_PROGRESS
    | RequestStatus.COMPLETED
    | RequestStatus.DENIED
    | RequestStatus.CANCELED;

export const pharmacyStatusValues: PharmacyStatus[] = [
    RequestStatus.PENDING_APPROVAL,
    RequestStatus.IN_PROGRESS,
    RequestStatus.COMPLETED,
    RequestStatus.DENIED,
    RequestStatus.CANCELED,
];

export const surgeryStatusValues: SurgeryStatus[] = [
    RequestStatus.ANESTHESIOLOGIST_ACCEPTED,
    RequestStatus.PAYMENT_COMPLETED,
    // SHER-935: surgery's own list, like shoeStatusValues above, so the two post-VPA statuses
    // reach the surgical filter menu and no other. They cannot occur on a lab, radiology,
    // cordonnerie or pharmacy request, and offering them there would filter to nothing.
    RequestStatus.PENDING_PAYMENT_AFTER_VPA,
    RequestStatus.PAYMENT_COMPLETED_AFTER_VPA,
    RequestStatus.READY_FOR_SURGERY,
    RequestStatus.COMPLETED,
    RequestStatus.PENDING_ANESTHESIOLOGIST,
    RequestStatus.PENDING_PAYMENT,
    RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS,
    RequestStatus.ANESTHESIOLOGIST_REJECTED,
    RequestStatus.CHECKED_IN,
    RequestStatus.CANCELED
];


export const statusToColor = (status:RequestStatus) => {
    let colour = "#000";
    switch(status){
        case RequestStatus.PENDING_APPROVAL:
        case RequestStatus.PENDING_PAYMENT:
        case RequestStatus.IN_PROGRESS:
        case RequestStatus.PENDING_ANESTHESIOLOGIST:
        case RequestStatus.PENDING_PURCHASE_SURGERY_PROTHESIS:
        // SHER-935: the post-VPA halves join their own families. Owing money looks the same
        // whichever desk you are at; what differs is the label, which is the whole point.
        case RequestStatus.PENDING_PAYMENT_AFTER_VPA:
            colour = yellow[900];
            break;
        case RequestStatus.ACCEPTED:
            colour = blue[900];
            break;
        case RequestStatus.SOME_ACCEPTED:
        case RequestStatus.INCOMPLETE_ACCEPTED:
            colour = blue[600];
            break;

        case RequestStatus.COMPLETED:
        case RequestStatus.ANESTHESIOLOGIST_ACCEPTED:
        case RequestStatus.PAYMENT_COMPLETED:
        case RequestStatus.READY_FOR_SURGERY:
        case RequestStatus.PAYMENT_COMPLETED_AFTER_VPA:
            colour = green[700];
            break;
        // SHER-908: a deeper green than COMPLETED — the same family, because delivery is the
        // good end of the flow, but distinguishable from "made but not collected" at a glance
        // in a table where both appear.
        case RequestStatus.MATERIAL_DELIVERED:
            colour = green[900];
            break;
        case RequestStatus.CANCELED:
        case RequestStatus.DENIED:
        case RequestStatus.INCOMPLETE:
        case RequestStatus.ANESTHESIOLOGIST_REJECTED:
            colour = red[500];            
            break;  
    }
    return colour;
}

/**
 * SHER-935 — "is this request waiting to be paid?", asked in one place.
 *
 * Seven surfaces asked it as `status === PENDING_PAYMENT`: the money icon on a calendar
 * block, the billing link and quick-pay button beside it, the pending-payment modal, the
 * pending-appointments list, and the billing action and quick-pay button on the patient's
 * appointment table. Splitting the money status in two silently broke all seven for exactly
 * the patients the split is about — they owe the surgical balance and lost the means to pay
 * it. A shared predicate is what stops the next money status from doing that again.
 */
export function isAwaitingPayment(status: RequestStatus): boolean {
    return status === RequestStatus.PENDING_PAYMENT || status === RequestStatus.PENDING_PAYMENT_AFTER_VPA;
}

export function getRequestStatusName(status: RequestStatus): string {
    return RequestStatus[status];
}




/**
 * Calculates the total days for a request based on its type
 * @param request The request object
 * @returns The calculated number of days
 */
export const calculateTotalDays = (request: IRequest, requestServiceType:ServiceType): string => {

    if(request.completedAt){
        return "-";
    }
    let referenceDate = "createdAt";
        
    // Type-specific calculations
    switch (requestServiceType) {
        case ServiceType.SURGERY:
        referenceDate = "readyForSurgeryAt"
    }
    if(request[referenceDate as keyof IRequest]){
        const date = new Date(request[referenceDate as keyof IRequest]).getTime();
        const todayDate = new Date().getTime();
        const totalDays = Math.round((todayDate - date) / (1000 * 60 * 60 * 24));
        return totalDays.toString();
    }
    return "-";
}; 